import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { ColumnDef } from "../../core-components/shared/Grid"
import { ColGroupDef, GridApi, ColDef, ICellEditorParams } from "ag-grid-community"
import { smrtLinkDateFormat } from "./smrt-link-date-format"
import { LinkWithoutReload } from "./link-helper"

export type ColDefOrColGroupDef = ColDef | ColGroupDef

export const AG_GRID_DATE_FILTER_OPTIONS = ["equals", "notEqual", "lessThan", "greaterThan", "inRange"]
export const AG_GRID_NUMBER_FILTER_OPTIONS = ["equals", "notEqual", "lessThan", "lessThanOrEqual", "greaterThan", "greaterThanOrEqual", "inRange"]
export const AG_GRID_TEXT_FILTER_OPTIONS = ["contains", "notContains", "equals", "notEqual", "startsWith", "endsWith"]

export const dateDayComparator = (filterDate: Date, target: string) => {
    if (!target) return 0

    const cellDate = new Date(target);
    
    // Normalize both dates to midnight UTC to avoid timezone issues
    const filterYear = filterDate.getFullYear()
    const filterMonth = filterDate.getMonth()
    const filterDay = filterDate.getDate()
    
    const cellYear = cellDate.getFullYear()
    const cellMonth = cellDate.getMonth()
    const cellDay = cellDate.getDate()
    
    // Compare year, month, and day only
    if (cellYear < filterYear) return -1
    if (cellYear > filterYear) return 1
    if (cellMonth < filterMonth) return -1
    if (cellMonth > filterMonth) return 1
    if (cellDay < filterDay) return -1
    if (cellDay > filterDay) return 1
    
    return 0
}

export const caseInsensitiveComparator = (valueA: any, valueB: any) => {
    if (valueA == null) return -1
    if (valueB == null) return 1
    return valueA.toString().toLowerCase().localeCompare(valueB.toString().toLowerCase())
}

export const dateColumnDef = (colDef: ColDef, showSeconds: boolean = true, useUTC: boolean = false): ColDef => {

    return Object.assign(colDef, {
        valueFormatter: params =>  {
            const date = new Date(params.value)
            return smrtLinkDateFormat(date, showSeconds, useUTC)
        },
        filter: "agDateColumnFilter",
        filterParams: {
            comparator: dateDayComparator,
            buttons: ["clear", "reset"],
            filterOptions: AG_GRID_DATE_FILTER_OPTIONS
        }
    })
}

/**
 * Generates a date range string from an ag-grid date filter for use in filenames.
 * Returns formatted strings like "date1_to_date2", "up_to_date2", "starting_from_date1",
 * "on_date", or "excludes_date" depending on the filter type. Returns empty string if no date filter is applied.
 * 
 * @param filterModel - The ag-grid filter model object
 * @param fieldName - The field name to check for date filters (e.g., "timestamp")
 * @returns Formatted date range string or empty string
 */
export const getDateFilterString = (filterModel: any, fieldName: string): string => {
    const dateFilter = filterModel?.[fieldName]
    if (!dateFilter || dateFilter.filterType !== "date") {
        return ""
    }

    const filterType = dateFilter.type
    const dateFrom = dateFilter.dateFrom
    const dateTo = dateFilter.dateTo

    // Helper to extract date part (YYYY-MM-DD) from date strings
    const extractDate = (dateStr: string): string => {
        return dateStr.split(" ")[0] || dateStr.split("T")[0]
    }

    if (filterType === "inRange" && dateFrom && dateTo) {
        const fromDate = extractDate(dateFrom)
        const toDate = extractDate(dateTo)
        return `${fromDate}_to_${toDate}`
    } else if (filterType === "lessThan" && dateFrom) {
        // lessThan means "up to" the date
        const toDate = extractDate(dateFrom)
        return `up_to_${toDate}`
    } else if (filterType === "greaterThan" && dateFrom) {
        // greaterThan means "starting from" the date
        const fromDate = extractDate(dateFrom)
        return `starting_from_${fromDate}`
    } else if (filterType === "equals" && dateFrom) {
        // equals means "on" the specific date
        const date = extractDate(dateFrom)
        return `on_${date}`
    } else if (filterType === "notEqual" && dateFrom) {
        // notEqual means "excludes" the specific date
        const date = extractDate(dateFrom)
        return `excludes_${date}`
    }

    return ""
}


export const numberColumnDef = (colDef: ColDef ): ColDef => {
    return Object.assign(colDef, {
        filter: "agNumberColumnFilter",
        filterParams: {
            filterOptions: AG_GRID_NUMBER_FILTER_OPTIONS,
            maxNumConditions: 1,
            buttons: ["clear", "reset"]
        },
        // note: cellType: "number" from ag-grid is broken, so we need to use our own comparator
        comparator: (valueA, valueB) => {
            const numA = parseFloat(valueA)
            const numB = parseFloat(valueB)
            
            // Handle null/undefined/NaN cases
            if (isNaN(numA) && isNaN(numB)) return 0
            if (isNaN(numA)) return 1
            if (isNaN(numB)) return -1
            
            return numA - numB
        }
    })
}

export const equalsColumnDef = (colDef: ColDef ): ColDef => {
    return Object.assign(colDef, {
        filterParams: {
            filterOptions: ["equals"]
        }
    })
}

export const createLinkCellRenderer = ( getLink: (data: any) => string) => {
    return (row: any) => {
        if (!row.value) {
            return ""
        }
        const link = getLink(row.data)
        return LinkWithoutReload(link, row.value)
    }
}

export const scrollPositionLabel = (gridApi: GridApi): string => {
    // NOTE: assumes rowBuffer = 0 in gridOptions
    if (!gridApi) {
        return ""
    }
    let total = gridApi.getDisplayedRowCount()
    if (total === 0) {
        return ""
    }
    const first = gridApi.getFirstDisplayedRowIndex() + 1
    const last = gridApi.getLastDisplayedRowIndex() + 1
    if (!gridApi["infiniteRowModel"] || gridApi.isLastRowIndexKnown()) {
        return `Displaying rows ${first} to ${last} out of ${total}`
    } else if (first === 1 && last === 1) {
        return ""
    } else {
        return `Displaying rows ${first} to ${last} out of ${total} (scroll to load more)`
    }
}

export const cloneColDefs = (colDefs: ColumnDef[]) => {
    return colDefs.map( colDef => {
        const newColDef = Object.assign({}, colDef)

        const children = (colDef as ColGroupDef).children

        if (children && children.length > 0) {
            newColDef["children"] = cloneColDefs(children)
        }
        return newColDef
    })
}

export const quickFilterHandler = (api: GridApi, event: any) => {
    const filter = event.target.value
    if (api) {
        api.setQuickFilter(filter)
    }
}

export const gridDragOver = (event: any) => {
    try {
        const dragSupported = event.dataTransfer.types.length;
        if (dragSupported) {
            event.dataTransfer.dropEffect = "move";
            event.preventDefault();
        }
    } catch {
        // Do nothing.  This is reached harmlessly in cypress tests
        // because somehow the event doesn't get the dataTransfer (though it works!).
    }
};

export interface DragBetweenGridsEvent {
    event: any
    leftOrRight: "left" | "right"
    rightGridApi: GridApi
    leftGridApi: GridApi
    idField: string
}
export const dragBetweenGridsDropHandler = (dbgEvent: DragBetweenGridsEvent) => {

    const {event, leftOrRight, rightGridApi, leftGridApi, idField} = dbgEvent
    event.preventDefault();

    const userAgent = window.navigator.userAgent;
    const isIE = userAgent.indexOf("Trident/") >= 0;
    const jsonData = event.dataTransfer.getData(isIE ? "text" : "application/json");
    const data = JSON.parse(jsonData);

    // if data missing or data has no id, do nothing
    if (!data || data[idField] == null) {
        return;
    }

    const sourceGridApi = leftOrRight === "left" ? rightGridApi : leftGridApi
    const destinationGridApi = leftOrRight === "left" ? leftGridApi : rightGridApi

    const sourceNode = sourceGridApi.getRowNode(data[idField])

    // If we are dropping into the destinationGrid, verify that the source is really the sourceGrid
    if (!sourceNode ) {
        return
    }

    // add the current node to the set of selected nodes, if it's not there already
    sourceNode.setSelected(true)

    const sourceRows = sourceGridApi.getSelectedRows()

    const destinationRows = []
    for (let row of sourceRows) {
        const rowAlreadyInGrid = !!destinationGridApi.getRowNode(row[idField]);
        if (!rowAlreadyInGrid) {
            destinationRows.push(row)
        }
    }
    if (destinationRows.length > 0) {
        sourceGridApi.applyTransaction({remove: destinationRows});
        destinationGridApi.applyTransaction({add: destinationRows});
    }
};

export const getGroupIds = (colDefs: ColDefOrColGroupDef[]): string[] => {

    const getGroupIdsIter = (colDefs: ColDefOrColGroupDef[], currentIds: string[]): string[] => {
        let ids = [...currentIds]
        for (let colDef of colDefs) {
            if ( "children" in colDef) {
                ids.push(colDef.groupId)
                ids = getGroupIdsIter( (colDef as ColGroupDef).children, ids)
            }
        }
        return ids
    }
    return getGroupIdsIter(colDefs, [])
}

export interface CheckboxHeaderComponentProps {
    isHeaderChecked: boolean
    onToggle: (boolean) => void
    id: string
}

export const CheckboxHeaderComponent = (props: CheckboxHeaderComponentProps) => {

    const [checked, setChecked] = useState<boolean>(props.isHeaderChecked)

    useEffect(() => {
        setChecked(props.isHeaderChecked)
    }, [props.isHeaderChecked])

    const onChange = event => {
        const elementChecked = (event.target as HTMLInputElement).checked
        setChecked(elementChecked)
        props.onToggle(elementChecked)
    }

    return <input type="checkbox" id={props.id} checked={checked} onChange={onChange}></input>
}

export const EmptyHeaderComponent = () => {
    return <span></span>
}

export const loadingMarkup = (inner: string) =>
`<span class="ag-overlay-loading-center" style="pointer-events: auto;">${inner}</span>`

export const tooltipHeaderTemplate = (): string => {
    return `<div class="ag-cell-label-container" role="presentation">
                    <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                    <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                        <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                        <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                        <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                        <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                        <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                        <span class="circle-badge info-badge ml-1 badge badge-light">
                            <span class="circle-badge-inner">
                                <span class="oi oi-info"></span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle class="fill-info" cx="8" cy="8" r="8" />
                                </svg>
                            </span>
                        </span>
                        <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                    </div>
                </div>`
}

interface CellEditorWithOnChangeProps extends ICellEditorParams {
    onValueChange?: (newValue: any) => void;
}

export const CellEditorWithOnChange = forwardRef((props: CellEditorWithOnChangeProps, ref) => {

    const [value, setValue] = useState<string>(props.value)
    
    const inputRef = useRef(null)

    useEffect(() => {
      inputRef.current.focus()
    }, [])

    useImperativeHandle(ref, () => ({
        getValue: () => {
          return value
        }
      }));

      useEffect(() => {
        if (props.api) {
          props.api.startEditingCell({
            rowIndex: props.rowIndex,
            colKey: props.column.getColId()
          });
        }
      }, [])


    const onChange = (event) => {
        const newValue = event.target.value;
        setValue(newValue)
        props.node.setDataValue(props.column.getColId(), newValue)
        if (props.onValueChange) {
            props.onValueChange(newValue)
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          props.stopEditing();
        }
      }

    return (
        <input
            ref={inputRef}
            type="text"
            className="pb-ag-grid-edittable-cell"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}

        />
    )
})